import React, { useRef, useState } from 'react';
import { Modal } from 'antd';
import { useController } from 'react-hook-form';
import { DocumentLine } from '@/components/common/atoms/DocumentLine';
import { Col, Container, Flex } from '@/ui/layout';
import styled from 'styled-components';
import { Body3Light, Label2, Span } from '@/ui/typography';
import { COLORS } from '@/utils/static/colors';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/common/atoms';
import { useSelector } from 'react-redux';
import { usePopups } from '@/hooks';

let camera_stream = null;
let media_recorder = null;
let blobs_recorded = [];
const video_type = MediaRecorder.isTypeSupported('video/webm')
  ? { type: 'video/webm', extension: 'webm' }
  : { type: 'video/mp4', extension: 'mp4' };

export const RecordVideo = ({ label, required, doc, setDoc, error }) => {
  const { t } = useTranslation();
  const { loader } = useSelector((state) => state.entities);
  const { showLoader, hideLoader, showErrorToast } = usePopups();
  const [active, setActive] = useState(false);
  const [video, setVideo] = useState(null);
  const [videoError, setVideoError] = useState('');
  const ref = useRef();

  const getCameraStream = async () => {
    try {
      camera_stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      ref.current.srcObject = camera_stream;
      if (videoError) setVideoError('');
      return camera_stream;
    } catch (e) {
      console.error(e);
      const notAllowedError = e?.name === 'NotAllowedError';
      setVideoError(
        notAllowedError
          ? t('Access to camera is denied.') +
              '\n' +
              t('Please provide access to the camera in your browser settings.')
          : t("Can't get access to the camera")
      );
    }
  };

  const openModal = async () => {
    setActive(true);
    if (video) setVideo(null);
    await getCameraStream();
  };

  const startRecord = async () => {
    try {
      const stream = camera_stream ? camera_stream : await getCameraStream();

      media_recorder = new MediaRecorder(stream, { mimeType: video_type.type });
      media_recorder.ondataavailable = (e) => {
        blobs_recorded.push(e.data);
      };

      media_recorder.start(200);
      showLoader('Processing');
    } catch (e) {
      console.error(e);
      showErrorToast(e, true);
    }
  };

  const stopStream = () => {
    try {
      if (media_recorder) {
        media_recorder.stop();
        media_recorder.ondataavailable = null;
      }

      const tracks = ref.current.srcObject.getTracks();
      tracks.forEach((track) => {
        track.stop();
      });
      ref.current.srcObject = null;
    } catch (e) {
      console.error(e);
      showErrorToast(e, true);
    }
  };

  const stopRecord = () => {
    stopStream();
    hideLoader();

    const file = new File(blobs_recorded, `recorded_video.${video_type.extension}`, {
      type: video_type.type
    });
    setVideo(file);

    camera_stream = null;
    media_recorder = null;
    blobs_recorded = [];
  };

  const saveVideo = () => {
    if (video) {
      setDoc(video);
      setActive(false);
    }
  };

  const closeModal = () => {
    stopStream();
    setActive(false);
  };

  return (
    <div>
      {label && (
        <Label2>
          {label}
          {required && <Span $color={COLORS.aragonBlue}>&nbsp;*</Span>}
        </Label2>
      )}

      {doc && (
        <DocumentLine doc={{ name: doc.name }} onDelete={() => setDoc(null)} hideLastBorder />
      )}

      <Container $marginTop={20}>
        <Button text={t('Record video')} onClick={openModal} size={'small'} />
      </Container>

      {error && (
        <Body3Light $color={COLORS.red} $marginTop={12}>
          {error.message}
        </Body3Light>
      )}

      <Modal open={active} onCancel={closeModal}>
        <Video ref={ref} autoPlay $hidden={video && !loader.visible} />
        {video && !loader.visible && (
          <Video src={URL.createObjectURL(video)} autoPlay loop controls />
        )}
        {videoError && (
          <Body3Light $color={COLORS.red} $preWrap>
            {videoError}
          </Body3Light>
        )}

        <Flex justify={'center'} $margin={-20} $marginTop={20} wrap>
          <Col $auto>
            <Button
              text={t('Save')}
              onClick={saveVideo}
              type={'primary'}
              disabled={!video || loader.visible}
            />
          </Col>
          <Col $auto>
            <Button
              text={t('Start')}
              onClick={startRecord}
              disabled={videoError || loader.visible}
            />
          </Col>
          <Col $auto>
            <Button text={t('Stop')} onClick={stopRecord} disabled={!loader.visible} />
          </Col>
        </Flex>
      </Modal>
    </div>
  );
};

export const RecordVideoForm = ({ label, required, form }) => {
  const {
    field: { ref, ...field },
    fieldState: { error }
  } = useController({
    ...form,
    defaultValue: null
  });

  return (
    <RecordVideo
      label={label}
      required={required}
      doc={field.value}
      setDoc={(video) => field.onChange(video)}
      error={error}
    />
  );
};

const Video = styled.video`
  width: 100%;
  height: 320px;
  ${(p) => (p.$hidden ? 'display: none;' : '')}
`;
